import * as z from "zod";
import { userFacingError } from "../authorization";
import { ensureSlug } from "../connectorSlug";
import { zql } from "../generatedDrizzleSchema";
import { actorColumns, creatorColumns } from "../rbac/roles";
import { insertWorkflowSkeleton } from "./createWorkflow";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

export const DERIVED_WORKFLOW_PLACEHOLDER_PROMPT =
  "Building workflow from chat…";

export const deriveWorkflowFromChatSchema = z.object({
  personalChatId: z.string(),
  workflowId: clientGeneratableIdSchema,
  branchId: clientGeneratableIdSchema,
  commitId: clientGeneratableIdSchema,
  chatId: clientGeneratableIdSchema,
  userEntryId: clientGeneratableIdSchema,
  spaceId: z.string(),
  clientSentAt: z.number().int().optional(),
  clientTimezone: z.string().optional(),
});

export const deriveWorkflowFromChat = mutator({
  schema: deriveWorkflowFromChatSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      personalChatId,
      workflowId,
      space,
      branchId,
      commitId,
      chatId,
      userEntryId,
    },
  }) => {
    const { userId } = ctx;
    const [personalChat] = await tx.run(
      zql.personalChats.where("id", personalChatId).where("userId", userId)
    );
    if (!personalChat) throw userFacingError("Personal chat not found");
    if (personalChat.derivedWorkflowId) {
      throw userFacingError("Chat already has a workflow");
    }

    await insertWorkflowSkeleton({
      tx,
      creator: creatorColumns(ctx),
      actor: actorColumns(ctx),
      tenantId: ctx.tenantId,
      workflowId,
      spaceId: space.id,
      branchId,
      commitId,
      chatId,
      userEntryId,
      name: "Untitled",
      prompt: DERIVED_WORKFLOW_PLACEHOLDER_PROMPT,
    });

    await tx.mutate.personalChats.update({
      id: personalChatId,
      derivedWorkflowId: workflowId,
    });

    const associations = await tx.run(
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("refId", personalChatId)
    );
    for (const assoc of associations) {
      const [app] = await tx.run(
        zql.connectedApps.where("id", assoc.connectedAppId)
      );
      if (!app) continue;
      await ensureSlug(
        tx,
        workflowId,
        app.id,
        app.identifier,
        app.name,
        ctx.tenantId
      );
    }
  },
});
